import React, { useState } from 'react';
import { useTheme } from '../../../theme/ThemeProvider';

const TableElement = ({ 
  element, 
  processedData,  // Datos procesados del backend
  isEditing = false 
}) => {
  const { mode } = useTheme();
  const config = element.config || {};
  
  // Extraer datos procesados del backend
  const tableData = processedData?.data || [];
  const columns = processedData?.columns || [];
  const totalRows = processedData?.total_rows || 0;
  const displayedRows = processedData?.displayed_rows || 0;
  const error = processedData?.error;
  
  // Estado para paginación local (si necesario)
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  const maxRows = config.maxRows || 20;
  const showPagination = config.showPagination !== false;
  const showSearch = config.showSearch !== false;
  
  // Filtrar por búsqueda si está habilitada
  const filteredData = searchTerm
    ? tableData.filter(row =>
        Object.values(row).some(val =>
          String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : tableData;
  
  // Paginación local si hay más datos de los que se muestran
  const startIdx = currentPage * maxRows;
  const endIdx = startIdx + maxRows;
  const paginatedData = filteredData.slice(startIdx, endIdx);
  const totalPages = Math.ceil(filteredData.length / maxRows);
  
  const formatCellValue = (value, column) => {
    if (value === null || value === undefined) {
      return '-';
    }
    
    const columnFormats = config.columnFormats || {};
    const format = columnFormats[column];
    
    if (!format) {
      return String(value);
    }
    
    if (format.type === 'number') {
      const num = parseFloat(value);
      if (isNaN(num)) return value;
      return num.toLocaleString(undefined, {
        minimumFractionDigits: format.decimals || 0,
        maximumFractionDigits: format.decimals || 0
      });
    }
    
    if (format.type === 'currency') {
      const num = parseFloat(value);
      if (isNaN(num)) return value;
      return `$${num.toLocaleString(undefined, {
        minimumFractionDigits: format.decimals || 2,
        maximumFractionDigits: format.decimals || 2
      })}`;
    }
    
    if (format.type === 'percentage') {
      const num = parseFloat(value);
      if (isNaN(num)) return value;
      return `${num.toFixed(format.decimals || 1)}%`;
    }
    
    return String(value);
  };
  
  const styles = {
    container: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    controls: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 'var(--spacing-sm) 0',
      marginBottom: 'var(--spacing-sm)',
      gap: 'var(--spacing-sm)',
      flexWrap: 'wrap'
    },
    searchBox: {
      padding: 'var(--spacing-sm) var(--spacing-md)',
      borderRadius: 'var(--border-radius-md)',
      border: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-white)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      fontSize: 'var(--font-size-sm)',
      outline: 'none',
      minWidth: '200px'
    },
    info: {
      fontSize: 'var(--font-size-sm)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)'
    },
    tableWrapper: {
      flex: 1,
      overflow: 'auto',
      borderRadius: 'var(--border-radius-md)',
      border: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 'var(--font-size-sm)'
    },
    thead: {
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-light-gray)',
      position: 'sticky',
      top: 0,
      zIndex: 1
    },
    th: {
      padding: 'var(--spacing-sm) var(--spacing-md)',
      textAlign: 'left',
      fontWeight: 'var(--font-weight-semibold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      borderBottom: `2px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-medium-gray)'}`,
      whiteSpace: 'nowrap'
    },
    td: {
      padding: 'var(--spacing-sm) var(--spacing-md)',
      borderBottom: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-dark-gray)'
    },
    tr: {
      transition: 'background-color 0.2s ease'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 'var(--spacing-sm)',
      padding: 'var(--spacing-md) 0',
      marginTop: 'var(--spacing-sm)'
    },
    pageButton: {
      padding: 'var(--spacing-xs) var(--spacing-sm)',
      borderRadius: 'var(--border-radius-sm)',
      border: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      fontSize: 'var(--font-size-sm)',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    pageButtonActive: {
      backgroundColor: '#29B5E8',
      color: '#FFFFFF',
      borderColor: '#29B5E8'
    },
    placeholder: {
      textAlign: 'center',
      color: mode === 'dark' ? 'var(--dark-text-tertiary)' : 'var(--color-medium-gray)',
      padding: 'var(--spacing-3xl)'
    },
    placeholderIcon: {
      fontSize: '48px',
      marginBottom: 'var(--spacing-md)',
      opacity: 0.5
    },
    error: {
      color: '#FF6B35',
      padding: 'var(--spacing-md)',
      textAlign: 'center'
    }
  };
  
  // Renderizado condicional
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          ⚠️ {error}
        </div>
      </div>
    );
  }
  
  if (!processedData || tableData.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.placeholder}>
          <div style={styles.placeholderIcon}>📋</div>
          <div>{isEditing ? 'Configura la tabla' : 'Sin datos disponibles'}</div>
        </div>
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      {/* Controles */}
      <div style={styles.controls}>
        {showSearch && (
          <input
            type="text"
            placeholder="🔍 Buscar..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(0);
            }}
            style={styles.searchBox}
          />
        )}
        <div style={styles.info}>
          Mostrando {paginatedData.length} de {filteredData.length} filas
          {totalRows > displayedRows && ` (${totalRows} total en servidor)`}
        </div>
      </div>
      
      {/* Tabla */}
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr>
              {columns.map((column, idx) => (
                <th key={idx} style={styles.th}>
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                style={styles.tr}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = mode === 'dark' 
                    ? 'rgba(41, 181, 232, 0.1)' 
                    : 'rgba(41, 181, 232, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {columns.map((column, colIdx) => (
                  <td key={colIdx} style={styles.td}>
                    {formatCellValue(row[column], column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Paginación */}
      {showPagination && totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            style={styles.pageButton}
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
          >
            ← Anterior
          </button>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i;
            } else if (currentPage < 3) {
              pageNum = i;
            } else if (currentPage > totalPages - 3) {
              pageNum = totalPages - 5 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <button
                key={pageNum}
                style={{
                  ...styles.pageButton,
                  ...(pageNum === currentPage ? styles.pageButtonActive : {})
                }}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum + 1}
              </button>
            );
          })}
          
          <button
            style={styles.pageButton}
            onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
            disabled={currentPage >= totalPages - 1}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default TableElement;
